/**
 * Airlines that no longer have dedicated pages. URLs containing these slugs
 * redirect to the equivalent generic page (route, city, from-city, landing, etc.).
 */
const DISCONTINUED_AIRLINE_URL_PHRASES = ["vistara", "trujet"];

const FLIGHTS_LANDING = "flights/";

/** Canonical flights_url_phrase for route redirects when legacy slugs differ. */
const ROUTE_CITY_URL_PHRASE_ALIASES = {
  delhi: "new_delhi",
  "new-delhi": "new_delhi",
};

const GI_ROUTE_LANGUAGE_SEGMENTS = ["hi"];

/** Map legacy/hotel city slugs (e.g. delhi) to flights_url_phrase (new_delhi). */
export const resolveRouteCityPhrase = (phrase) =>
  ROUTE_CITY_URL_PHRASE_ALIASES[String(phrase || "").toLowerCase()] || phrase;

const normalizeGoibiboRoutePrefix = (prefix) => {
  const prefixParts = String(prefix || "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);

  if (prefixParts[0] === "international-flights" && prefixParts[1]) {
    const languageSegment = prefixParts.find((part) => GI_ROUTE_LANGUAGE_SEGMENTS.includes(part));
    return `international-flights/${languageSegment ? `${languageSegment}/` : ""}`;
  }

  return `${prefixParts.join("/")}/`;
};

/** Build generic route redirect path, e.g. flights/new_delhi-mumbai-cheap-airtickets.html */
export const getRouteCheapAirticketsRedirectPath = ({
  prefix = "flights/",
  src,
  dest,
  isGoibibo = false,
}) => {
  if (!src || !dest) return null;

  if (isGoibibo) {
    return `${normalizeGoibiboRoutePrefix(prefix)}${src}-to-${dest}-flights/`;
  }

  const shouldUseCityAliases = String(prefix || "").startsWith("flights/");
  const canonicalSrc = shouldUseCityAliases ? resolveRouteCityPhrase(src) : src;
  const canonicalDest = shouldUseCityAliases ? resolveRouteCityPhrase(dest) : dest;
  return `${prefix}${canonicalSrc}-${canonicalDest}-cheap-airtickets.html`;
};

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const buildPathPrefix = ({ lob, params }) => {
  const prefixParts = [lob || "flights"];
  if (params?.length > 1) {
    prefixParts.push(...params.slice(0, -1));
  }
  return `${prefixParts.join("/")}/`;
};

const getRedirectFromSegment = ({ segment, prefix, isAirlineRoute, params, isGoibibo }) => {
  if (!segment) return null;

  for (const airline of DISCONTINUED_AIRLINE_URL_PHRASES) {
    const sourcePattern = escapeRegex(airline);

    if (isAirlineRoute && params.some((p) => p.toLowerCase() === airline)) {
      return FLIGHTS_LANDING;
    }

    // vistara-to-{city}-flights → flights-to-{city} (Goibibo)
    let match = segment.match(new RegExp(`^${sourcePattern}-to-([a-z0-9_-]+)-flights$`, "i"));
    if (match) {
      return `${prefix}flights-to-${match[1]}`;
    }

    // flights-from-{city|country}-{airline} → flights-from-{city|country} (Goibibo)
    match = segment.match(new RegExp(`^flights-from-([a-z0-9_-]+)-${sourcePattern}$`, "i"));
    if (match) {
      return `${prefix}flights-from-${match[1]}`;
    }

    // {src}-{dest}-{airline}-tickets|flights.html → {src}-{dest}-cheap-airtickets.html (route)
    // Must run before the from-city rule; otherwise delhi-mumbai-vistara-tickets is
    // misread as city=delhi-mumbai.
    match = segment.match(
      new RegExp(
        `^([a-z0-9_-]+)-([a-z0-9_-]+)-${sourcePattern}-(tickets|flights)\\.html$`,
        "i",
      ),
    );
    if (match) {
      return getRouteCheapAirticketsRedirectPath({
        prefix,
        src: match[1],
        dest: match[2],
        isGoibibo,
      });
    }

    // {city|country}-{airline}-tickets.html → flights-from-{city|country}.html
    match = segment.match(
      new RegExp(`^([a-z0-9_-]+)-${sourcePattern}-tickets\\.html$`, "i"),
    );
    if (match) {
      const city = resolveRouteCityPhrase(match[1]);
      return `${prefix}flights-from-${city}.html`;
    }

    // City-airline pages (e.g. mumbai-vistara-flights.html) → {city}-flight-tickets.html
    match = segment.match(
      new RegExp(`^([a-z0-9_-]+)-${sourcePattern}-flights\\.html$`, "i"),
    );
    if (match) {
      const city = resolveRouteCityPhrase(match[1]);
      return `${prefix}${city}-flight-tickets.html`;
    }

    // {src}-to-{dest}-{airline}-airline-schedule → {src}-to-{dest}-flight-schedule (Goibibo)
    match = segment.match(
      new RegExp(`^([a-z0-9_-]+)-to-([a-z0-9_-]+)-${sourcePattern}-airline-schedule$`, "i"),
    );
    if (match) {
      return `${prefix}${match[1]}-to-${match[2]}-flight-schedule`;
    }

    // Airline index / static pages → flights landing
    if (new RegExp(`^${sourcePattern}(-|$)`, "i").test(segment)) {
      return FLIGHTS_LANDING;
    }
  }

  return null;
};

/**
 * Resolves a 301 redirect path (no scheme/host) for discontinued-airline URLs.
 */
export const getMergedAirlineRedirectPath = ({
  lob = "flights",
  params = [],
  isAirlineRoute = false,
  isGoibibo = false,
}) => {
  if (!params?.length) return null;

  const prefix = buildPathPrefix({ lob, params });
  const segment = params[params.length - 1];

  return getRedirectFromSegment({ segment, prefix, isAirlineRoute, params, isGoibibo });
};

export const mergedAirlineRedirectFromUrl = (url, { isGoibibo = false } = {}) => {
  if (!url) return null;

  let path = url;
  if (path[0] === "/") {
    path = path.substring(1);
  }

  const parts = path.split("/").filter(Boolean);
  if (!parts.length) return null;

  let lob = "flights";
  let isAirlineRoute = false;
  let params = parts;

  if (parts[0] === "international-flights") {
    lob = "international-flights";
    params = parts.slice(1);
  } else if (parts[0] === "airlines") {
    isAirlineRoute = true;
    params = parts.slice(1);
  } else if (parts[0] === "flights") {
    params = parts.slice(1);
  }

  if (!params.length) return null;

  const redirect = getMergedAirlineRedirectPath({ lob, params, isAirlineRoute, isGoibibo });
  return redirect || null;
};
